import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import SEO from "@/components/site/SEO";
import { blogPostingSchema, upsertArticleJsonLd } from "@/components/site/JsonLd";
import { dedupeHeadNow } from "@/components/site/HeadDedupe";
import api from "@/lib/api";
import ShareButtons from "@/components/site/ShareButtons";
import DOMPurify from "dompurify";
import { Focus, X, Download, FileText, Sparkles, Feather, Mic, BookOpen, MessagesSquare, School as SchoolIcon, ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { getSchoolUrl } from "@/lib/slug";
import { track } from "@/lib/analytics";
import { useFlags } from "@/lib/flags";
import { ArticleDiscussion } from "@/components/site/ArticleDiscussion";

// Relative time label — "aktif 2 jam lalu", "aktif hari ini", "aktif kemarin".
// Matches the copy in the Fase-1 mockup ("Lanjutkan diskusinya di forum" block).
const _timeAgoLabel = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const diffMin = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 60000));
  if (diffMin < 1) return "aktif baru saja";
  if (diffMin < 60) return `aktif ${diffMin} menit lalu`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `aktif ${diffH} jam lalu`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "aktif kemarin";
  if (diffD < 7) return `aktif ${diffD} hari lalu`;
  return "aktif minggu ini";
};

// Small header used by both inline and sticky TOC variants.
const TocHeader = () => (
  <div className="flex items-center gap-2 mb-3">
    <span className="inline-block w-1 h-1 rounded-full bg-[#D2673E]" aria-hidden="true" />
    <div className="text-[11px] uppercase tracking-widest text-[#BE5730] font-semibold">
      Daftar Isi
    </div>
  </div>
);

// Timeline-style TOC. Each item sits along a thin left-border rail;
// the active heading swaps the rail segment to coral + bolder text.
// Font: Plus Jakarta Sans (site body font) for a clean, professional nav — NOT the editorial serif.
const TocList = ({ items, activeId }) => (
  <ul className="border-l border-[#EAE7E0] font-sans" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
    {items.map((it, i) => {
      const active = activeId === it.id;
      const isSub = it.level === 3;
      return (
        <li key={it.id} data-testid={`blog-toc-item-${i}`} className="relative">
          {/* Coral rail segment that mirrors the active item */}
          <span
            aria-hidden="true"
            className={
              "absolute -left-px top-0 bottom-0 w-[2px] transition-colors duration-300 ease-out " +
              (active ? "bg-[#D2673E]" : "bg-transparent")
            }
          />
          <a
            href={`#${it.id}`}
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(it.id);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              if (window.history?.replaceState) {
                window.history.replaceState(null, "", `#${it.id}`);
              }
            }}
            data-active={active || undefined}
            data-level={it.level}
            className={
              "block pr-1 py-1.5 leading-snug transition-colors duration-300 ease-out " +
              (isSub ? "pl-6 text-[13px] " : "pl-3 text-[14px] ") +
              (active
                ? "text-[#D2673E] font-semibold"
                : "text-[#5C6472] hover:text-[#1B2B4D]")
            }
          >
            {it.text}
          </a>
        </li>
      );
    })}
  </ul>
);

// Turn arbitrary heading text into a URL-safe anchor id (deterministic).
const slugifyAnchor = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60) || "bagian";

// Parse the sanitized HTML, inject unique `id` attrs onto every <h2>,
// and return both the enriched HTML and the extracted TOC entries.
const buildToc = (sanitizedHtml) => {
  if (typeof window === "undefined" || !sanitizedHtml) return { html: sanitizedHtml, items: [] };
  const doc = new DOMParser().parseFromString(`<div>${sanitizedHtml}</div>`, "text/html");
  const root = doc.body.firstChild;
  const items = [];
  const seen = new Map();
  // Include BOTH H2 and H3 so the TOC mirrors the full outline; H3 is rendered
  // with slight indentation in TocList for visual hierarchy.
  root.querySelectorAll("h2, h3").forEach((el) => {
    const text = (el.textContent || "").trim();
    if (!text) return;
    let base = slugifyAnchor(text);
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    const id = n === 1 ? base : `${base}-${n}`;
    el.setAttribute("id", id);
    items.push({ id, text, level: el.tagName === "H3" ? 3 : 2 });
  });
  return { html: root.innerHTML, items };
};

const CONTENT_TYPE_MAP = {
  editorial: { label: "Editorial",  fg: "#5B21B6", bg: "#EDE9FE", icon: Feather },
  insight:   { label: "Insight",    fg: "#1E3A8A", bg: "#DBEAFE", icon: Sparkles },
  laporan:   { label: "Laporan",    fg: "#BE5730", bg: "#F6E7DF", icon: FileText },
  wawancara: { label: "Wawancara",  fg: "#3B6D46", bg: "#E4EFE6", icon: Mic },
  feature:   { label: "Feature",    fg: "#8A5A00", bg: "#FFF8E6", icon: BookOpen },
};

/**
 * Split article HTML into two chunks at a paragraph-safe boundary so an inline
 * "Baca juga" component can be rendered between them WITHOUT mutating the
 * stored TipTap body. Returns `null` when no safe split is possible.
 *
 * Boundary rules (Phase 2 spec):
 *   • only split BETWEEN top-level siblings — never inside a paragraph, list,
 *     blockquote, figure, heading, or preformatted block;
 *   • target ~35–50% of visible text length so the chip sits in the reading
 *     flow, not up-top;
 *   • require ≥ 3 top-level children AND ≥ 800 chars of text total, otherwise
 *     the article is too short and we skip inline entirely.
 */
const splitBodyForInline = (bodyHtml, { targetRatio = 0.42 } = {}) => {
  if (typeof window === "undefined" || !bodyHtml) return null;
  const doc = new DOMParser().parseFromString(`<div>${bodyHtml}</div>`, "text/html");
  const root = doc.body.firstChild;
  if (!root) return null;
  const kids = Array.from(root.children);
  if (kids.length < 3) return null;
  const totalText = (root.textContent || "").replace(/\s+/g, " ").trim();
  if (totalText.length < 800) return null;
  const totalLen = totalText.length;
  const target = totalLen * targetRatio;
  // Elements safe to split AFTER (block-level, not opening a group).
  const SAFE_AFTER = new Set(["P", "H2", "H3", "H4", "BLOCKQUOTE", "FIGURE", "HR"]);
  let acc = 0;
  let bestIdx = -1;
  for (let i = 0; i < kids.length; i++) {
    const el = kids[i];
    const len = (el.textContent || "").replace(/\s+/g, " ").trim().length;
    acc += len;
    // Never split as the very last child (would leave the chip stranded at
    // the bottom with no article after it).
    if (i >= kids.length - 1) break;
    if (acc < target) continue;
    if (!SAFE_AFTER.has(el.tagName)) continue;
    bestIdx = i;
    break;
  }
  // Fallback: if we overshot the target without finding a safe boundary,
  // walk backwards for the last safe P/H before the target.
  if (bestIdx === -1) {
    let cum = 0;
    for (let i = 0; i < kids.length - 1; i++) {
      cum += (kids[i].textContent || "").length;
      if (cum > target) break;
      if (SAFE_AFTER.has(kids[i].tagName)) bestIdx = i;
    }
  }
  if (bestIdx === -1) return null;
  const firstParts = [];
  const secondParts = [];
  kids.forEach((el, i) => {
    (i <= bestIdx ? firstParts : secondParts).push(el.outerHTML);
  });
  if (!firstParts.length || !secondParts.length) return null;
  return { firstHtml: firstParts.join(""), secondHtml: secondParts.join("") };
};

/** Compact inline "Baca juga" chip — placed between two body chunks. */
const BacaJugaInline = ({ post, sourceSlug }) => {
  if (!post) return null;
  // Reuse the same window.dataLayer convention as BottomTabBar / DiscussionCard /
  // RuangHome — no new analytics infrastructure is introduced. Gracefully no-ops
  // when dataLayer is absent.
  const trackClick = () => {
    track("click_read_more", {
      source_slug: sourceSlug,
      target_slug: post.slug,
    });
  };
  return (
    <aside
      className="not-prose my-8 rounded-xl border border-[#e6e9f3] bg-[#fbfaf6] p-4 sm:p-5"
      data-testid="blog-baca-juga-inline"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#eef1fb] flex items-center justify-center">
          <BookOpen size={16} strokeWidth={1.9} className="text-[#2f45a6]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#4c5666] mb-1">
            Baca juga
          </div>
          <Link
            to={`/jurnal/${post.slug}`}
            data-testid="blog-baca-juga-inline-link"
            onClick={trackClick}
            className="group inline-flex items-start gap-1.5 text-[15px] leading-snug font-medium text-[#26307d] hover:text-[#d2643c] transition-colors"
          >
            <span className="line-clamp-2">{post.title}</span>
            <ArrowUpRight size={14} strokeWidth={2.25} className="flex-shrink-0 mt-1 opacity-70 group-hover:opacity-100" />
          </Link>
        </div>
      </div>
    </aside>
  );
};

export default function BlogDetail({ previewId } = {}) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [focus, setFocus] = useState(false);
  const [relatedThreads, setRelatedThreads] = useState([]);
  const { standalone_forum_enabled: forumEnabled } = useFlags();
  // Phase 2 — Journal ↔ Journal recommendations. Rank #1 → inline "Baca juga",
  // ranks #2–#4 → end "Artikel Terkait". Fetched from a single backend call so
  // no duplication logic lives in the client.
  const [relatedArticles, setRelatedArticles] = useState([]);
  // Scroll-spy: id of the H2 currently in view (drives the coral TOC rail).
  const [activeTocId, setActiveTocId] = useState(null);
  // Reading progress: 0..100 based on how much of the article body has scrolled past.
  const [readingProgress, setReadingProgress] = useState(0);
  // Distance (px) from the top of the grid to the first line of body copy, so
  // we can push the sticky TOC down to align with the drop-cap paragraph.
  const [tocOffset, setTocOffset] = useState(0);

  // Editor preview (/pratinjau/:previewId): admin-only snapshot of the editor
  // form. Never touches the public article, its JSON-LD, related widgets or
  // discussion (slug param is undefined on this route, so those effects skip).
  useEffect(() => {
    if (!previewId) return;
    setP(null);
    setPreviewError(null);
    api.get(`/admin/blog-previews/${previewId}`)
      .then((r) => setP(r.data))
      .catch((e) => setPreviewError(
        e.response?.status === 401 || e.response?.status === 403
          ? "Pratinjau hanya dapat dibuka oleh editor/admin yang sudah masuk."
          : e.response?.data?.detail || "Pratinjau tidak dapat dimuat.",
      ));
  }, [previewId]);

  useEffect(() => {
    if (previewId) return;
    api.get(`/blog/${slug}`).then((r) => {
      const data = r.data;
      // Slug History Redirect: if the server resolved this URL via a rename
      // history, silently swap the browser URL to the canonical slug. `replace`
      // keeps the old URL out of the back-stack so shared links still work but
      // the address bar never shows a stale slug.
      if (data && data.slug && data.slug !== slug) {
        navigate(`/jurnal/${data.slug}`, { replace: true });
        return;
      }
      setP(data);
    });
  }, [slug, navigate, previewId]);

  // Runtime BlogPosting JSON-LD ownership (Sep 2026, deterministic):
  // upserts the single stable-id element #article-blogposting-jsonld.
  //  - Prerendered article: the snapshot's element content is REPLACED in
  //    place with the live values (edits surface without a rebuild).
  //  - Post-build article (no snapshot): the element is created on data load.
  //  - Either way the final rendered HTML holds exactly ONE current
  //    BlogPosting. Cleanup removes it on unmount/slug change so no stale
  //    schema leaks onto other pages. Also runs the synchronous head safety
  //    net at the exact commit where article metadata becomes available.
  useEffect(() => {
    if (!p || !p.slug || previewId) return;
    const canonicalUrl = `${window.location.origin}/jurnal/${p.slug}`;
    const el = upsertArticleJsonLd(blogPostingSchema(p, canonicalUrl));
    dedupeHeadNow();
    return () => {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [p, previewId]);
  // Cross-Discovery: pull community threads linked to this article's schools/tags
  useEffect(() => {
    if (!slug || !forumEnabled) { setRelatedThreads([]); return; }
    api.get(`/blog/${slug}/related-threads`, { params: { limit: 5 } })
      .then((r) => setRelatedThreads(r.data || []))
      .catch(() => setRelatedThreads([]));
  }, [slug, forumEnabled]);

  // Phase 2 — Journal ↔ Journal recommendations (deterministic scoring:
  // shared_schools > shared_categories > freshness). Empty result when the
  // article has no metadata to match or no candidate clears the gate.
  useEffect(() => {
    if (!slug) return;
    api.get(`/blog/${slug}/related-articles`, { params: { limit: 4 } })
      .then((r) => setRelatedArticles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRelatedArticles([]));
  }, [slug]);

  useEffect(() => {
    if (focus) document.body.classList.add("reader-focus");
    else document.body.classList.remove("reader-focus");
    const onKey = (e) => { if (e.key === "Escape") setFocus(false); };
    if (focus) window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("reader-focus");
      window.removeEventListener("keydown", onKey);
    };
  }, [focus]);

  // Scroll spy — real-time active-heading tracking for the TOC coral rail.
  //
  // Fixed in Iter 93: uses IntersectionObserver ONLY (no scroll-listener +
  // getBoundingClientRect fallback). The previous fallback iterated headings
  // during initial mount when the DOM wasn't laid out yet — all elements
  // reported `top=0`, tricking the loop into picking the LAST heading.
  //
  // Strategy:
  //  1. Poll for heading readiness (rich HTML is injected via
  //     dangerouslySetInnerHTML AFTER React commits, so headings may not
  //     exist on the very first tick). Retries up to 2 seconds.
  //  2. IntersectionObserver with a "reading zone" (top 88px cut so it starts
  //     just below the sticky nav; bottom 60% cut so only headings in the top
  //     40% of the viewport are considered "intersecting").
  //  3. On each callback, update a Set of visible ids. Then:
  //     - If any heading is currently visible → pick the FIRST in doc order.
  //     - Else (deep inside a section between two headings) → pick the LAST
  //       heading whose bottom has scrolled past the sticky top (y=88), so
  //       the rail stays anchored to the current section.
  //     - Else (before the very first heading) → clear activeId (no highlight).
  useEffect(() => {
    if (!p) return;
    let observer;
    let cleanupScroll;
    let rafId = null;
    let retryTimer;
    let attempts = 0;

    const setup = () => {
      const body = document.querySelector('[data-testid="blog-body"]');
      const initialHeadings = body ? Array.from(body.querySelectorAll("h2[id], h3[id]")) : [];
      if (initialHeadings.length === 0) {
        if (attempts++ < 10) {
          retryTimer = setTimeout(setup, 200);
        }
        return;
      }

      const visibleIds = new Set();

      // Re-query headings LIVE from the DOM on every recompute — cached refs
      // go stale when React re-renders the article body (dangerouslySetInnerHTML
      // swaps the DOM subtree). Reading rect from a detached node returns 0
      // and would trigger our "reader is at top" branch by mistake.
      const currentHeadings = () => {
        const b = document.querySelector('[data-testid="blog-body"]');
        return b ? Array.from(b.querySelectorAll("h2[id], h3[id]")) : [];
      };

      const recompute = () => {
        const hs = currentHeadings();
        if (hs.length === 0) return;
        const firstVisible = hs.find((h) => visibleIds.has(h.id));
        if (firstVisible) {
          setActiveTocId(firstVisible.id);
          return;
        }
        for (let i = hs.length - 1; i >= 0; i--) {
          const rect = hs[i].getBoundingClientRect();
          if (rect.height === 0) continue;
          if (rect.top + rect.height < 88) {
            setActiveTocId(hs[i].id);
            return;
          }
        }
        setActiveTocId(null);
      };

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleIds.add(entry.target.id);
          else visibleIds.delete(entry.target.id);
        });
        recompute();
      }, {
        rootMargin: "-88px 0px -60% 0px",
        threshold: 0,
      });
      initialHeadings.forEach((h) => observer.observe(h));

      const onScroll = () => {
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          recompute();
        });
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanupScroll = () => {
        window.removeEventListener("scroll", onScroll);
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    };

    setup();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (observer) observer.disconnect();
      if (cleanupScroll) cleanupScroll();
    };
  }, [p, slug]);

  // Reading progress — track how much of the article body has scrolled past.
  useEffect(() => {
    if (!p) return;
    const onScroll = () => {
      const body = document.querySelector('[data-testid="blog-body"]');
      if (!body) return;
      const rect = body.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.5;
      const scrolled = Math.max(0, -rect.top);
      const pct = total > 0 ? Math.min(100, (scrolled / total) * 100) : 0;
      setReadingProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [p, slug]);

  // Measure how far below the top of the grid the article body starts, so the
  // sticky TOC can be pushed down to align with the drop-cap paragraph rather
  // than with the article title. Recomputes on load & resize.
  useEffect(() => {
    if (!p) return;
    const measure = () => {
      // Measure body.top relative to the ASIDE's own top (not the outer grid),
      // because CSS padding on the outer container already offsets the aside —
      // measuring vs the grid border would double-count that padding.
      const aside = document.querySelector('[data-testid="blog-detail-page"] > aside');
      const body = document.querySelector('[data-testid="blog-body"]');
      if (!aside || !body) return;
      // Temporarily reset any previous padding so the measurement is stable
      // across re-renders that already applied an offset.
      const prev = aside.style.paddingTop;
      aside.style.paddingTop = "0px";
      // Force a reflow so the reset is reflected in getBoundingClientRect.
      // eslint-disable-next-line no-unused-expressions
      aside.offsetHeight;
      const offset = body.getBoundingClientRect().top - aside.getBoundingClientRect().top;
      // Restore the previous inline style before React re-renders with new state.
      aside.style.paddingTop = prev;
      setTocOffset(Math.max(0, Math.round(offset)));
    };
    // Delay to allow images & fonts to lay out.
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 500);
    window.addEventListener("resize", measure);
    // Recompute after the cover image finishes loading (biggest layout shifter).
    const cover = document.querySelector('[data-testid="blog-detail-page"] img');
    if (cover) cover.addEventListener("load", measure, { once: true });
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", measure);
    };
  }, [p, slug, focus]);

  // Pre-parse the body once so we can render TOC in the left column
  // WITHOUT re-parsing later inside the article body.
  // NOTE: All hooks MUST run before any early return (React Rules of Hooks),
  // so this block + the useMemos below execute unconditionally. When `p` is
  // still loading, `raw` is empty and every derived value is a safe default.
  const raw = p?.body || "";
  const isHtml = /<(p|h[1-6]|ul|ol|li|blockquote|strong|em|a|br|hr|img|pre|code|aside|figure|figcaption)\b/i.test(raw);
  let bodyHtml = "";
  let tocItems = [];
  if (isHtml) {
    const clean = DOMPurify.sanitize(raw, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ["figure", "figcaption", "aside"],
      ADD_ATTR: ["data-pull-quote", "data-image-figure"],
    });
    const parsed = buildToc(clean);
    bodyHtml = parsed.html;
    tocItems = parsed.items;
  }
  const showToc = !focus && tocItems.length >= 2;

  // Phase 2 — decide inline vs end recommendations from a single ranked list.
  // Rank #0 → inline "Baca juga", ranks #1–#3 → end "Artikel Terkait". Split
  // guarantees zero duplication between the two surfaces.
  const currentSlug = p?.slug;
  const inlineRec = useMemo(() => {
    const first = (relatedArticles || []).find((r) => r && r.slug && r.slug !== currentSlug);
    return first || null;
  }, [relatedArticles, currentSlug]);
  const endRecs = useMemo(() => {
    return (relatedArticles || [])
      .filter((r) => r && r.slug && r.slug !== currentSlug && r.slug !== (inlineRec && inlineRec.slug))
      .slice(0, 3);
  }, [relatedArticles, currentSlug, inlineRec]);
  const bodySplit = useMemo(() => {
    if (focus) return null; // hide inline chip in reader mode
    if (!isHtml || !inlineRec) return null;
    return splitBodyForInline(bodyHtml, { targetRatio: 0.42 });
  }, [isHtml, bodyHtml, inlineRec, focus]);

  if (previewError) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-[#4B5563]" data-testid="blog-preview-error">
        <SEO title="Pratinjau artikel | The School Reports" robots="noindex,nofollow" />
        {previewError}
      </div>
    );
  }
  if (!p) return <div className="max-w-3xl mx-auto p-10 text-[#4B5563]">Memuat…</div>;

  const articleMaxW = focus ? "max-w-[68ch]" : "max-w-3xl";
  const bodyText = focus ? "text-[19px] leading-[1.9]" : "text-[17px] leading-[1.85]";
  const meta = CONTENT_TYPE_MAP[p.content_type] || CONTENT_TYPE_MAP.insight;
  const TypeIcon = meta.icon;
  const isPdf = p.content_type === "laporan" && p.pdf_url;

  // Related discussions markup — used both in the xl+ right sidebar and
  // as an inline stack block below <xl (mobile, tablet).
  const relatedBlock = (
    !focus && relatedThreads.length > 0 ? (
      <section
        data-testid="blog-related-threads"
        className="mt-14 pt-8 border-t border-[#EAE7E0] xl:mt-0 xl:pt-0 xl:border-0"
      >
        <div className="flex items-center gap-2 mb-1">
          <MessagesSquare size={16} className="text-[#D2673E]" strokeWidth={2.25} />
          <div className="text-xs uppercase tracking-widest text-[#BE5730] font-semibold">Diskusi Komunitas Terkait</div>
        </div>
        <p className="text-sm text-[#5C6472] leading-relaxed max-w-xl">
          Apa yang orang tua bicarakan di forum tentang sekolah dan topik dalam artikel ini.
        </p>
        <ul className="mt-5 space-y-3">
          {relatedThreads.map((t, i) => (
            <li
              key={t.thread_id}
              data-testid={`blog-related-thread-${i}`}
              className="rounded-2xl border border-[#E8E4DB] bg-white hover:border-[#D2673E] hover:shadow-sm transition-all"
            >
              <Link to={`/forum/${t.thread_id}`} className="block p-4 group">
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#8A8F99]">
                  {t.school_name && (
                    <span
                      className="inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full bg-[#F6E7DF]/60 border border-[#F3D9C7] text-[#8A5B3F]"
                      title={t.school_name}
                    >
                      <SchoolIcon size={10} strokeWidth={2.5} className="text-[#BE5730]/80" />
                      <span className="text-[10px] font-medium tracking-tight">#&nbsp;{t.school_name}</span>
                    </span>
                  )}
                  <span className="uppercase tracking-widest">{t.category}</span>
                  <span>· {t.reply_count || 0} balasan</span>
                  <span>· {(t.user_name || "").split(" ")[0] || "Anonim"}</span>
                </div>
                <div className="mt-1.5 text-[15px] font-semibold text-[#1B2B4D] group-hover:text-[#D2673E] leading-snug tracking-tight">
                  {t.title}
                </div>
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to="/forum"
          data-testid="blog-related-threads-see-all"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#D2673E] hover:text-[#BE5730]"
        >
          Lihat semua diskusi di forum <ArrowRight size={14} strokeWidth={2.25} />
        </Link>
      </section>
    ) : null
  );

  // On xl+ (≥1280px) we use a 3-column grid: TOC | Article | Related sidebar.
  // In focus reader mode we always fall back to a single centred column.
  const outerGrid = focus
    ? "max-w-[68ch] mx-auto px-6 lg:px-10 py-12 transition-all duration-300"
    : "max-w-[80rem] mx-auto px-6 lg:px-10 py-12 transition-all duration-300 xl:grid xl:grid-cols-[14rem_minmax(0,44rem)_16rem] xl:gap-x-10";

  return (
    <>
      {/* Draft preview banner — only shown to admins (server-gated) who reach
          this page for an unpublished article. Placed OUTSIDE the article grid
          so it always renders as a full-width strip above the TOC / body columns,
          not stuck in a grid cell. */}
      {(previewId || p?.status === "draft") && (
        <div
          data-testid="draft-preview-banner"
          className="max-w-[80rem] mx-auto px-6 lg:px-10 pt-6 -mb-4"
        >
          <div className="rounded-2xl border-2 border-[#E6B754] bg-[#FFFBEE] px-5 py-3 flex items-center gap-3">
            <div className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-[#F5D889] text-[#7A4F00] px-3 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#E6B754]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B0781F]" aria-hidden="true" />
              {previewId ? "Pratinjau Editor" : "Pratinjau Draf"}
            </div>
            <div className="text-sm text-[#7A4F00] leading-snug" data-testid="draft-preview-banner-text">
              {previewId ? (
                <>Ini pratinjau isi editor saat ini{p.status === "published" ? " (artikel tayang tidak diubah)" : " — artikel belum dipublikasikan"}. Hanya editor/admin yang dapat membukanya.</>
              ) : (
                <>Artikel ini <strong>belum dipublikasikan</strong>. Hanya admin yang bisa mengaksesnya lewat pratinjau. Klik <em>Terbitkan</em> di dasbor untuk menayangkan.</>
              )}
            </div>
          </div>
        </div>
      )}
    <div data-testid="blog-detail-page" className={outerGrid}>
      {/* Reading progress — slim coral bar fixed to the very top of the viewport.
          Hidden in focus mode (reader chrome is already stripped).
          Also hidden for plain-text posts where the fixed-top bar tends to
          overlap the site header banner. */}
      {!focus && isHtml && (
        <div
          data-testid="blog-reading-progress"
          aria-hidden="true"
          className="fixed top-0 left-0 right-0 h-[3px] bg-transparent z-40 pointer-events-none"
        >
          <div
            className="h-full bg-[#D2673E] transition-[width] duration-100 ease-out"
            style={{ width: `${readingProgress}%` }}
          />
        </div>
      )}

      {/* ── Left column — Sticky TOC on xl+ ─────────────────────── */}
      {!focus && (
        <aside
          className="hidden xl:block xl:col-start-1 xl:row-start-1"
          style={{ paddingTop: tocOffset ? `${tocOffset}px` : undefined }}
        >
          {showToc && (
            <nav
              data-testid="blog-toc-sticky"
              aria-label="Daftar isi (sticky)"
              className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto p-1"
            >
              <TocHeader />
              <TocList items={tocItems} activeId={activeTocId} />
            </nav>
          )}
        </aside>
      )}

      {/* ── Center column — Article content ──────────────────────── */}
      <article
        className={
          (focus ? "" : "xl:col-start-2 xl:row-start-1 ") +
          (focus ? articleMaxW + " mx-auto" : "")
        }
      >
      {(() => {
        const title = `${p.title} | The Reports — The School Reports`;
        const desc = (p.excerpt || p.summary || (p.body || "").replace(/<[^>]+>/g, " ").replace(/[#*_>`]/g, "").replace(/\s+/g, " ").trim().slice(0, 160)) || "Artikel The Reports — edukasi & insight sekolah di Indonesia.";
        const canonical = typeof window !== "undefined" ? `${window.location.origin}/jurnal/${p.slug}` : undefined;
        return (
          <>
            {previewId
              ? <SEO title={`Pratinjau: ${title}`} description={desc} image={p.hero_image_url || p.cover_url} type="article" robots="noindex,nofollow" />
              : <SEO title={title} description={desc} image={p.hero_image_url || p.cover_url} type="article" canonical={canonical} />}
            {/* BlogPosting JSON-LD is NOT rendered via Helmet here — it is
                upserted deterministically into #article-blogposting-jsonld by
                the useEffect above (see JsonLd.upsertArticleJsonLd). That
                covers post-build articles, replaces prerender snapshot content
                in place for edited articles, and guarantees exactly one
                script element at all times. */}
          </>
        );
      })()}
      <div className="flex items-center justify-between gap-3">
        <Link to="/" className={"text-sm text-[#4B5563] hover:text-[#E07A5F] " + (focus ? "opacity-70" : "")} data-testid="back-to-blog">← Kembali ke The Reports</Link>
        {!focus ? (
          <button
            type="button"
            onClick={() => setFocus(true)}
            data-testid="blog-focus-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAE7E0] bg-white text-xs text-[#1F2937] hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-colors"
            title="Mode fokus baca"
          >
            <Focus size={13} /> Fokus baca
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setFocus(false)}
            data-testid="blog-focus-exit"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs hover:bg-[#1E40AF] transition-colors"
            title="Keluar dari mode fokus (Esc)"
          >
            <X size={13} /> Keluar fokus <span className="text-[10px] opacity-70 hidden sm:inline">Esc</span>
          </button>
        )}
      </div>

      {/* Content type badge */}
      <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="blog-detail-meta">
        <span className="inline-flex items-center rounded-full text-[11px] px-2 py-0.5 gap-1.5 font-bold uppercase tracking-widest"
          style={{ background: meta.bg, color: meta.fg }}>
          <TypeIcon size={12} strokeWidth={2.25} />
          {meta.label}
        </span>
        {p.tags && p.tags.length > 0 && (
          <span className="text-xs text-[#8A8F99] uppercase tracking-widest">{p.tags.join(" · ")}</span>
        )}
      </div>

      <h1 className={"headline mt-3 " + (focus ? "!text-5xl lg:!text-7xl" : "")}>{p.title}</h1>

      {/* Author profile block */}
      <div className="mt-6 flex items-center gap-3" data-testid="blog-detail-author">
        {p.author_avatar_url ? (
          <img src={p.author_avatar_url} alt={p.author_name}
            className="w-12 h-12 rounded-full object-cover border border-[#E8E4DB]" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] font-bold flex items-center justify-center">
            {(p.author_name || "").split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
        )}
        <div>
          <div className="text-sm font-semibold text-[#1B2B4D]">{p.author_name || "Redaksi"}</div>
          {p.author_title && (
            <div className="text-[11px] uppercase tracking-widest text-[#D2673E] font-bold" data-testid="blog-author-title">
              {p.author_title}
            </div>
          )}
          <div className="text-xs text-[#8A8F99] mt-0.5">
            {new Date(p.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}
            {p.read_time_min ? ` · ${p.read_time_min} mnt baca` : ""}
          </div>
        </div>
      </div>

      {/* PDF download banner — high-priority for 'laporan' content_type */}
      {isPdf && !focus && (
        <div className="mt-6 rounded-2xl border border-[#F6E7DF] bg-[#F6E7DF]/50 p-5 flex items-center justify-between gap-4 flex-wrap"
          data-testid="blog-pdf-banner">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center border border-[#F6E7DF]">
              <FileText size={22} className="text-[#D2673E]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#1B2B4D]">Laporan lengkap tersedia dalam PDF</div>
              <div className="text-xs text-[#5C6472] mt-0.5">
                Unduh untuk membaca offline & bagikan dengan sekolah Anda.
                {p.pdf_size_bytes ? ` (${(p.pdf_size_bytes / (1024 * 1024)).toFixed(1)} MB)` : ""}
              </div>
            </div>
          </div>
          <a
            href={p.pdf_url}
            target="_blank"
            rel="noreferrer"
            data-testid="blog-pdf-download"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D2673E] hover:bg-[#BE5730] text-white text-sm font-semibold transition-colors whitespace-nowrap"
          >
            <Download size={16} /> Unduh PDF
          </a>
        </div>
      )}

      {p.cover_url && !focus && (
        <img src={p.cover_url} alt="" className="mt-8 rounded-2xl w-full max-h-[70vh] object-cover border border-[#EAE7E0]" />
      )}

      {/* Key stats strip (below cover) */}
      {(p.key_stats || []).length > 0 && !focus && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="blog-key-stats">
          {p.key_stats.slice(0, 4).map((s, i) => (
            <div key={i} className="p-4 rounded-2xl bg-white border border-[#E8E4DB] text-center"
              data-testid={`blog-key-stat-${i}`}>
              <div className="font-serif-display text-3xl text-[#D2673E] leading-none">
                {s.value}{s.unit ? <span className="text-lg">{s.unit}</span> : null}
              </div>
              <div className="mt-1.5 text-[11px] text-[#5C6472] leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {(() => {
        if (!isHtml) {
          return (
            <div className={"mt-8 drop-cap text-[#1F2937] " + bodyText} data-testid="blog-body">
              <div className="whitespace-pre-wrap">{raw}</div>
            </div>
          );
        }
        return (
          <>
            {/* Inline TOC — visible on <xl only (mobile, tablet, medium laptop). */}
            {showToc && (
              <nav
                data-testid="blog-toc"
                aria-label="Daftar isi"
                className="xl:hidden mt-10 rounded-2xl border border-[#EAE7E0] bg-[#FBFAF6] p-5"
              >
                <TocHeader />
                <TocList items={tocItems} activeId={activeTocId} />
              </nav>
            )}
            <div className={"mt-8 drop-cap text-[#1F2937] " + bodyText} data-testid="blog-body">
              {bodySplit ? (
                <div className="prose max-w-none prose-headings:font-serif-display prose-headings:text-[#26307d] prose-h2:text-[28px] prose-h2:leading-snug prose-h2:mt-14 prose-h2:mb-4 prose-h2:scroll-mt-24 prose-h3:text-xl prose-h3:font-semibold prose-h3:leading-snug prose-h3:mt-10 prose-h3:mb-3 prose-h3:scroll-mt-24 prose-p:text-[#232a36] prose-p:text-[17px] prose-p:leading-[1.72] prose-p:my-4 prose-li:text-[#232a36] prose-li:text-[17px] prose-li:leading-[1.72] prose-strong:text-[#232a36] prose-blockquote:border-l-[3px] prose-blockquote:border-[#2f45a6] prose-blockquote:bg-transparent prose-blockquote:not-italic prose-blockquote:font-serif-display prose-blockquote:italic prose-blockquote:text-[#4c5666] prose-blockquote:rounded-none prose-blockquote:pl-6 prose-a:text-[#2f45a6] prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:border prose-img:border-[#e6e9f3] prose-code:text-[#2f45a6] prose-code:bg-[#eef1fb] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-[#26307d] prose-pre:text-[#eef1fb] prose-pre:rounded-xl prose-pre:p-4">
                  <div dangerouslySetInnerHTML={{ __html: bodySplit.firstHtml }} />
                  <BacaJugaInline post={inlineRec} sourceSlug={p.slug} />
                  <div dangerouslySetInnerHTML={{ __html: bodySplit.secondHtml }} />
                </div>
              ) : (
                <div
                  className="prose max-w-none prose-headings:font-serif-display prose-headings:text-[#26307d] prose-h2:text-[28px] prose-h2:leading-snug prose-h2:mt-14 prose-h2:mb-4 prose-h2:scroll-mt-24 prose-h3:text-xl prose-h3:font-semibold prose-h3:leading-snug prose-h3:mt-10 prose-h3:mb-3 prose-h3:scroll-mt-24 prose-p:text-[#232a36] prose-p:text-[17px] prose-p:leading-[1.72] prose-p:my-4 prose-li:text-[#232a36] prose-li:text-[17px] prose-li:leading-[1.72] prose-strong:text-[#232a36] prose-blockquote:border-l-[3px] prose-blockquote:border-[#2f45a6] prose-blockquote:bg-transparent prose-blockquote:not-italic prose-blockquote:font-serif-display prose-blockquote:italic prose-blockquote:text-[#4c5666] prose-blockquote:rounded-none prose-blockquote:pl-6 prose-a:text-[#2f45a6] prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:border prose-img:border-[#e6e9f3] prose-code:text-[#2f45a6] prose-code:bg-[#eef1fb] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-[#26307d] prose-pre:text-[#eef1fb] prose-pre:rounded-xl prose-pre:p-4"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              )}
            </div>
          </>
        );
      })()}

      {/* Related discussions — inline stack on <xl only. */}
      <div className="xl:hidden">{relatedBlock}</div>

      {/* THE SCHOOL REPORTS TAKEAWAY — editorial callout above the tag pills.
          Iter 92 restyle: soft blue panel (--panel #eef1fb) + blue uppercase eyebrow
          per DEV_INSTRUCTIONS_article_restyle. Serif body for editorial voice. */}
      {!focus && p.takeaway && p.takeaway.trim() && (
        <div className="mt-10" data-testid="blog-takeaway-box">
          <div
            className="rounded-xl bg-[#eef1fb] py-6 px-6 sm:px-7"
          >
            <div
              className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#26307d] mb-2"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
              data-testid="blog-takeaway-title"
            >
              The School Reports Takeaway
            </div>
            <div
              className="text-[16px] leading-[1.72] text-[#232a36] whitespace-pre-line"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
              data-testid="blog-takeaway-body"
            >
              {p.takeaway}
            </div>
          </div>
        </div>
      )}

      {/* Cross-Discovery Phase 1 — Article → School.
          Renders each school in `related_school_ids` (resolved server-side into
          `related_schools`) so readers can jump straight into the canonical
          /sekolah/... detail page. Section is hidden entirely when the article
          has no related schools, so legacy articles are unaffected. */}
      {!focus && Array.isArray(p.related_schools) && p.related_schools.length > 0 && (
        <div className="mt-10" data-testid="blog-related-schools">
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-3 flex items-center gap-1.5">
            <SchoolIcon size={12} strokeWidth={2} className="text-[#1E3A8A]" />
            Sekolah dalam artikel ini
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {p.related_schools.map((s, i) => {
              const levels = Array.isArray(s.education_levels) ? s.education_levels : (s.level ? [s.level] : []);
              const loc = [s.city, s.state].filter(Boolean).join(", ");
              return (
                <li key={s.school_id}>
                  <Link
                    to={getSchoolUrl(s)}
                    data-testid={`blog-related-school-${i}`}
                    className="group flex items-start gap-3 p-4 rounded-xl bg-white border border-[#EAE7E0] hover:border-[#1E3A8A] hover:bg-[#F5F7FE] transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
                      <SchoolIcon size={18} strokeWidth={1.75} className="text-[#1E3A8A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1F2937] leading-snug group-hover:text-[#1E3A8A] transition-colors line-clamp-2">
                        {s.name}
                      </div>
                      <div className="mt-1 text-xs text-[#4B5563] flex items-center gap-x-2 gap-y-1 flex-wrap">
                        {loc && <span>{loc}</span>}
                        {levels.length > 0 && (
                          <>
                            {loc && <span aria-hidden="true">·</span>}
                            <span className="text-[#1E3A8A] font-medium">{levels.join(" · ")}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={16} strokeWidth={2} className="flex-shrink-0 mt-1 text-[#4B5563] group-hover:text-[#1E3A8A] transition-colors" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Phase 2 — Journal ↔ Journal automatic cross-discovery.
          Ranks #2–#4 from the same recommendation call whose rank #1 already
          feeds the inline "Baca juga" chip above. Section hides entirely when
          there are no more candidates, so articles with a single relevant
          match still only show it inline (no half-empty grid). */}
      {!focus && endRecs.length > 0 && (
        <section className="mt-12" data-testid="blog-related-articles">
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-4 flex items-center gap-1.5">
            <BookOpen size={12} strokeWidth={2} className="text-[#D2673E]" />
            Artikel Terkait
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {endRecs.map((a, i) => (
              <li key={a.post_id}>
                <Link
                  to={`/jurnal/${a.slug}`}
                  data-testid={`blog-related-article-${i}`}
                  onClick={() => {
                    track("click_related_article", {
                      source_slug: p.slug,
                      target_slug: a.slug,
                      rank: i + 1,
                    });
                  }}
                  className="group block bg-white border border-[#E8E4DB] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(30,58,138,0.06)] transition-all duration-300"
                >
                  <div className="aspect-[16/10] bg-[#F3F1ED] overflow-hidden">
                    {a.cover_url
                      ? <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full flex items-center justify-center"><BookOpen size={22} className="text-[#4B5563]" /></div>}
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{new Date(a.created_at).toLocaleDateString("id-ID")}</div>
                    <div className="mt-1 font-serif-display text-[17px] leading-snug text-[#1F2937] group-hover:text-[#D2673E] transition-colors line-clamp-2">{a.title}</div>
                    {a.excerpt && <div className="text-xs text-[#4B5563] mt-1 line-clamp-2">{a.excerpt}</div>}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Tag pills — coral-accent, cross-discovery to /jurnal?tag=… */}
      {!focus && p.tags && p.tags.length > 0 && (
        <div className="mt-10 pt-8 border-t border-[#EAE7E0]" data-testid="blog-tags">
          <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-3">Tag</div>
          <div className="flex flex-wrap gap-2">
            {p.tags.map((t, i) => (
              <Link
                key={`${t}-${i}`}
                to={`/?tag=${encodeURIComponent(t)}`}
                data-testid={`blog-tag-pill-${i}`}
                className="inline-flex items-center gap-1 rounded-full border border-[#F3D9C7] bg-[#FDF6F0] hover:bg-[#F6E7DF] hover:border-[#D2673E] px-3 py-1 text-[12px] font-medium text-[#BE5730] transition-colors"
                title={`Lihat konten lain tentang “${t}”`}
              >
                <span aria-hidden="true">#</span>
                {t}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Fase 1 — Cross-discovery block: "Lanjutkan diskusinya di forum".
          Sits between the tag pills and the share buttons per spec, so readers
          get an exploration option BEFORE the exit-oriented share row. */}
      {!focus && forumEnabled && p.tags && p.tags.length > 0 && (() => {
        const topThreads = (relatedThreads || []).slice(0, 3);
        const seeAllTag = p.tags[0] || "";
        const primaryTag = p.tags.slice(0, 2).map((t) => `#${t}`).join(" & ") || "topik ini";
        return (
          <div className="mt-8" data-testid="blog-continue-forum">
            <div className="rounded-2xl bg-white border border-[#e6e9f3] p-6 sm:p-7">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#fbeee7] flex items-center justify-center">
                  <MessagesSquare size={20} strokeWidth={1.75} className="text-[#d2643c]" />
                </div>
                <div>
                  <h3 className="font-serif-display text-xl text-[#26307d] leading-tight">
                    Lanjutkan diskusinya di forum
                  </h3>
                  <p className="mt-1 text-sm text-[#8b93a4]">Orang tua lain sedang membahas topik ini.</p>
                </div>
              </div>

              {topThreads.length > 0 ? (
                <ul className="mt-5 space-y-2" data-testid="continue-forum-list">
                  {topThreads.map((t, i) => (
                    <li key={t.thread_id}>
                      <Link
                        to={`/forum/${t.thread_id}`}
                        data-testid={`continue-forum-item-${i}`}
                        className="group flex items-start gap-3 p-4 rounded-xl bg-[#fafbfe] hover:bg-[#eef1fb] border border-transparent hover:border-[#e6e9f3] transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#232a36] text-[15px] leading-snug group-hover:text-[#2f45a6] transition-colors">
                            {t.title}
                          </div>
                          <div className="mt-1.5 flex items-center flex-wrap gap-x-2 gap-y-1 text-xs text-[#8b93a4]">
                            <span className="inline-flex items-center gap-1">
                              <MessagesSquare size={12} strokeWidth={1.75} />
                              {t.reply_count || 0} balasan
                            </span>
                            <span aria-hidden="true">·</span>
                            <span>{_timeAgoLabel(t.last_activity || t.created_at)}</span>
                            {t.matched_tag && (
                              <>
                                <span aria-hidden="true">·</span>
                                <span className="text-[#2f45a6] font-medium" data-testid={`continue-forum-item-tag-${i}`}>#{t.matched_tag}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <ChevronRight size={16} strokeWidth={2} className="flex-shrink-0 mt-1 text-[#8b93a4] group-hover:text-[#2f45a6] transition-colors" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-5 p-4 rounded-xl bg-[#fafbfe] border border-dashed border-[#e6e9f3] text-center" data-testid="continue-forum-empty">
                  <p className="text-sm text-[#4c5666]">
                    Belum ada diskusi tentang <span className="font-semibold text-[#232a36]">{primaryTag}</span>.
                  </p>
                  <Link
                    to={`/forum?tag=${encodeURIComponent(seeAllTag)}`}
                    data-testid="continue-forum-start-cta"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#d2643c] hover:bg-[#B85529] text-white text-sm font-semibold px-4 py-2 transition-colors"
                  >
                    Mulai diskusi pertama <ArrowUpRight size={14} strokeWidth={2.25} />
                  </Link>
                </div>
              )}

              <Link
                to={`/forum?tag=${encodeURIComponent(seeAllTag)}`}
                data-testid="continue-forum-see-all"
                className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-[#e6e9f3] hover:border-[#2f45a6] bg-white hover:bg-[#fafbfe] text-[#4c5666] hover:text-[#2f45a6] text-sm font-medium px-5 py-2.5 transition-colors"
              >
                Lihat semua diskusi tentang {primaryTag} <ArrowUpRight size={13} strokeWidth={2.25} />
              </Link>
            </div>
          </div>
        );
      })()}

      {!focus && (
        <div className="mt-10 pt-8 border-t border-[#e6e9f3]" data-testid="blog-share">
          <div className="text-xs uppercase tracking-widest text-[#8b93a4] mb-3">Bagikan artikel ini</div>
          <ShareButtons title={`${p.title} — The School Reports`} testidPrefix="blog-share" variant="ghost" />
        </div>
      )}
      {!focus && !previewId && <ArticleDiscussion slug={p.slug} />}
      </article>

      {/* ── Right column — Related discussions on xl+ ────────────── */}
      {!focus && (
        <aside className="hidden xl:block xl:col-start-3 xl:row-start-1">
          {relatedBlock && (
            <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {relatedBlock}
            </div>
          )}
        </aside>
      )}
    </div>
    </>
  );
}
